import { CLASS_NAME } from 'constants/class-names'
import { ATTR_NAME } from 'constants/attr-name'
import { EventType } from 'constants/enums'
import { getFirstFormField, FormFields } from 'utils/dom'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { isMobile } from 'utils/system/device'
import { CarComparisonStore } from '../store'
import { isFindCompare } from 'utils/page'

export class VehicleFinderComponent extends ComponentBase {
    private desktopButton: HTMLButtonElement
    private mobileButton: HTMLElement
    private vehicleFinder: HTMLElement
    private firstFormField: FormFields
    private html: HTMLHtmlElement
    private scrollPosition: number

    public constructor(protected finder: HTMLDetailsElement) {
        super()
        if (!this.initElements()) {
            return
        }
        this.initButtons()
        this.initScroll()
        this.setVisibility()

        CarComparisonStore.getInstance().vehicleFinder = this
    }

    private initElements(): boolean {
        const desktopButton = this.getElement<HTMLButtonElement>(
            `.${CLASS_NAME.vehicleFinder.desktopToggle}`
        )
        const mobileButton = this.getElement(
            `.${CLASS_NAME.vehicleFinder.mobileToggle}`
        )
        const vehicleFinder = this.getElement(`.${CLASS_NAME.vehicleFinder._}`)
        const form = this.getElement<HTMLFormElement>(
            `.${CLASS_NAME.vehicleFinder.form}`,
            this.finder
        )

        if (!form) {
            return false
        }

        const firstFormField = getFirstFormField(form)

        const html = this.getElement<HTMLHtmlElement>('html')

        if (
            !desktopButton ||
            !mobileButton ||
            !vehicleFinder ||
            !firstFormField ||
            !html
        ) {
            return false
        }

        this.desktopButton = desktopButton
        this.mobileButton = mobileButton
        this.vehicleFinder = vehicleFinder
        this.firstFormField = firstFormField
        this.html = html
        return true
    }

    /**
     * Desktop scroll behaviour
     */
    private initScroll(): void {
        document.addEventListener(EventType.SCROLL, () => {
            const scroll = document.documentElement.scrollTop
            // eslint-disable-next-line no-magic-numbers
            if (scroll >= 300) {
                if (isMobile()) {
                    if (this.scrollingDown()) {
                        this.vehicleFinder.classList.add(
                            CLASS_NAME.vehicleFinder.minimizeOnScroll
                        )
                    } else {
                        this.vehicleFinder.classList.remove(
                            CLASS_NAME.vehicleFinder.minimizeOnScroll
                        )
                    }
                } else {
                    this.vehicleFinder.classList.add(
                        CLASS_NAME.vehicleFinder.minimizeOnScroll
                    )
                }
            } else {
                this.vehicleFinder.classList.remove(
                    CLASS_NAME.vehicleFinder.minimizeOnScroll
                )
            }
        })
    }

    /**
     * Desktop button click behaviour
     */
    private initButtons(): void {
        this.desktopButton.addEventListener(EventType.CLICK, () => {
            this.buttonClickAction()
        })

        this.mobileButton.addEventListener(EventType.CLICK, (event) => {
            this.buttonClickAction(event)
        })
    }

    /**
     * Actions when the button clicked
     * - toggle the component
     * - focus the field
     */
    private buttonClickAction(event?: Event): void {
        const show = !this.finder.hasAttribute(
            ATTR_NAME.driving.vehicleFinder.open
        )
        if (show) {
            this.open(event)
            return
        }
        this.close(event)
    }

    /**
     * Focus on to the first form field
     */
    private setFocus(): void {
        window.setTimeout(() => this.firstFormField.focus(), 0)
    }

    /**
     * Set visibility of the Vehicle Finder
     * @param {boolean} show
     */
    private setVisibility(): void {
        if (!this.hasUrlParams() && isFindCompare()) {
            this.open()
            return
        }
        this.close()
    }

    /**
     * Determine scroll direction
     * to use for docking sticky logic on mobile
     */
    private scrollingDown(): boolean {
        // Get the number of pixels the document is currently scrolled along the vertical axis
        const scrollDown =
            document.body.getBoundingClientRect().top < this.scrollPosition
        this.scrollPosition = document.body.getBoundingClientRect().top
        return scrollDown
    }

    /**
     * open vehicle finder
     */
    private open(event?: Event): void {
        if (event) {
            event.preventDefault()
        }
        this.setFocus()
        this.desktopButton.classList.add(CLASS_NAME.common.buttonActive)
        this.finder.setAttribute(ATTR_NAME.driving.vehicleFinder.open, '')
        this.html.classList.add(CLASS_NAME.vehicleFinder.wrapperOpen)
    }

    /**
     * close vehicle finder
     */
    public close(event?: Event): void {
        if (event) {
            event.preventDefault()
        }
        this.mobileButton.classList.remove(CLASS_NAME.common.buttonActive)
        this.desktopButton.classList.remove(CLASS_NAME.common.buttonActive)
        this.finder.removeAttribute(ATTR_NAME.driving.vehicleFinder.open)
        this.html.classList.remove(CLASS_NAME.vehicleFinder.wrapperOpen)
    }

    /**
     * Check if URL has Parameters
     */
    private hasUrlParams = (): boolean => window.location.href.includes('?')
}
